import {
    detectBrowser,
    detectOperatingSystem,
} from '../diagnostics/environment.js';

const numberOr = (value, fallback) => {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    return Number.isFinite(Number(value)) ? Number(value) : fallback;
};

const clamp = (value, minimum, maximum) => (
    Math.min(maximum, Math.max(minimum, value))
);

const even = (value) => Math.max(2, Math.round(value / 2) * 2);

function platformFromUserAgent(userAgent = '') {
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return 'ios';
    }

    if (/Android/i.test(userAgent)) {
        return 'android';
    }

    return 'desktop';
}

export function createAdaptiveProfile({
    navigatorLike = globalThis.navigator,
    windowLike = globalThis.window,
    camera = null,
} = {}) {
    const userAgent = navigatorLike?.userAgent || '';
    const platform = platformFromUserAgent(userAgent);
    const operatingSystem = detectOperatingSystem(
        userAgent,
        navigatorLike
    );
    const browser = detectBrowser(userAgent);
    const hardwareConcurrency = numberOr(
        navigatorLike?.hardwareConcurrency,
        4
    );
    const deviceMemory = numberOr(navigatorLike?.deviceMemory, 4);
    const screenWidth = numberOr(windowLike?.screen?.width, 1280);
    const screenHeight = numberOr(windowLike?.screen?.height, 720);
    const pixelRatio = clamp(
        numberOr(windowLike?.devicePixelRatio, 1),
        1,
        2
    );
    const largestViewportEdge = Math.max(screenWidth, screenHeight);
    const constrainedDevice = (
        hardwareConcurrency <= 4
        || deviceMemory <= 2
    );
    const cameraMaxWidth = numberOr(camera?.maxWidth, Infinity);
    const cameraMaxHeight = numberOr(camera?.maxHeight, Infinity);
    const performanceCeiling = constrainedDevice
        ? 1280
        : platform === 'ios'
            ? 1600
            : 1920;
    const desiredWidth = even(clamp(
        largestViewportEdge * pixelRatio,
        960,
        performanceCeiling
    ));
    const width = even(Math.min(
        desiredWidth,
        cameraMaxWidth,
        cameraMaxHeight * (16 / 9)
    ));
    const desiredHeight = even(width / (16 / 9));
    const height = even(Math.min(desiredHeight, cameraMaxHeight));
    const cameraMaxFps = numberOr(
        camera?.capabilities?.frameRate?.max,
        30
    );
    const idealFrameRate = clamp(
        constrainedDevice ? 24 : 30,
        15,
        cameraMaxFps
    );
    const supportedConstraints =
        navigatorLike?.mediaDevices?.getSupportedConstraints?.() || {};

    return {
        platform,
        operatingSystem,
        browser,
        hardwareConcurrency,
        deviceMemory,
        constrainedDevice,
        target: {
            width,
            height,
            frameRate: idealFrameRate,
            processingFrameRate: constrainedDevice
                ? 12
                : platform === 'ios'
                    ? 15
                    : 20,
        },
        supportedConstraints: {
            width: supportedConstraints.width !== false,
            height: supportedConstraints.height !== false,
            frameRate: supportedConstraints.frameRate !== false,
            facingMode: supportedConstraints.facingMode !== false,
            deviceId: supportedConstraints.deviceId !== false,
        },
        strategy: constrainedDevice
            ? 'balanced-performance'
            : 'quality-first',
    };
}

export function buildAdaptiveVideoConstraints({
    profile,
    deviceId = null,
    facingMode = null,
    relaxed = false,
} = {}) {
    if (relaxed) {
        if (deviceId) {
            return { deviceId: { exact: deviceId } };
        }

        if (facingMode) {
            return { facingMode: { ideal: facingMode } };
        }

        return true;
    }

    const constraints = {};
    const target = profile?.target || {};
    const supported = profile?.supportedConstraints || {};

    if (deviceId && supported.deviceId !== false) {
        constraints.deviceId = { exact: deviceId };
    } else if (facingMode && supported.facingMode !== false) {
        constraints.facingMode = { ideal: facingMode };
    }

    if (target.width && supported.width !== false) {
        constraints.width = { ideal: target.width };
    }

    if (target.height && supported.height !== false) {
        constraints.height = { ideal: target.height };
    }

    if (target.frameRate && supported.frameRate !== false) {
        constraints.frameRate = {
            ideal: target.frameRate,
        };
    }

    return constraints;
}
